import React, { useState } from 'react';
import { effectiveCost, effectiveSell, marginPct } from '@/lib/stockUtils';
import { PackMeta, SortTh } from './StockPrimitives';
import { Pencil, Check, X } from 'lucide-react';

const AllProductsTab = ({ allProducts, wacMap, waspMap, filtered, allSort, setAllSort, batchTrackingEnabled, onProductClick, onStockEdit }) => {
  const [editingStock, setEditingStock] = useState(null); // { catalogId, value }
  const [savingStock, setSavingStock]   = useState(false);

  const rows = filtered(allProducts).map(p => {
    const ec     = effectiveCost(p, wacMap);
    const es     = effectiveSell(p, waspMap);
    const margin = marginPct(ec, es);
    return { ...p, _effectiveCost: ec, _effectiveSell: es, _margin: margin };
  });

  const sorted = [...rows].sort((a, b) => {
    let av, bv;
    switch (allSort.col) {
      case 'name':   av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
      case 'stock':  av = a.stock ?? -Infinity; bv = b.stock ?? -Infinity; break;
      case 'wac':    av = a._effectiveCost ?? -Infinity; bv = b._effectiveCost ?? -Infinity; break;
      case 'wasp':   av = a._effectiveSell ?? -Infinity; bv = b._effectiveSell ?? -Infinity; break;
      case 'margin': av = a._margin ?? -Infinity; bv = b._margin ?? -Infinity; break;
      default:       av = 0; bv = 0;
    }
    if (av < bv) return allSort.dir === 'asc' ? -1 : 1;
    if (av > bv) return allSort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const colSpan = 6 + (batchTrackingEnabled ? 1 : 0);

  const startStockEdit = (e, p) => {
    e.stopPropagation();
    setEditingStock({ catalogId: p.catalog_id, value: String(p.stock ?? 0) });
  };

  const cancelStockEdit = (e) => {
    e?.stopPropagation();
    setEditingStock(null);
  };

  const saveStockEdit = async (e) => {
    e?.stopPropagation();
    if (!editingStock) return;
    const newStock = parseFloat(editingStock.value);
    if (isNaN(newStock) || newStock < 0) return;
    setSavingStock(true);
    try {
      await onStockEdit?.(editingStock.catalogId, newStock);
      setEditingStock(null);
    } finally {
      setSavingStock(false);
    }
  };

  return (
    <div className="overflow-x-auto w-full">
    <table className="w-full min-w-[680px] text-sm">
      <thead>
        <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-wide">
          <SortTh col="name"   label="Product"    sort={allSort} setSort={setAllSort} />
          <th className="text-left py-3 px-4 whitespace-nowrap">Pack Size</th>
          <SortTh col="stock"  label="Stock"      sort={allSort} setSort={setAllSort} right />
          <SortTh col="wac"    label="Avg Buy ₹"  sort={allSort} setSort={setAllSort} right sub="wtd avg" />
          <SortTh col="wasp"   label="Avg Sell ₹" sort={allSort} setSort={setAllSort} right sub="wtd avg" />
          <SortTh col="margin" label="Margin %"   sort={allSort} setSort={setAllSort} right className="pr-8" />
          {batchTrackingEnabled && <th className="text-left py-3 px-4">Expiry</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map(p => {
          const marginColor = p._margin == null ? '' : p._margin < 0 ? 'text-red-600' : p._margin < 10 ? 'text-amber-600' : 'text-emerald-700';
          const packLabel   = [p.pack_size, p.unit].filter(Boolean).join(' ') || '—';
          const isEditingThisStock = editingStock?.catalogId === p.catalog_id;

          return (
            <tr
              key={p.catalog_id || p.id}
              className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer group"
              onClick={() => !isEditingThisStock && onProductClick?.(p)}
            >
              <td className="py-3 px-4">
                <span className="font-medium">{p.name}</span>
                {p.sku && <div className="mt-0.5"><span className="text-[11px] text-muted-foreground/50">{p.sku}</span></div>}
              </td>
              <td className="py-3 px-4 text-xs text-muted-foreground">{packLabel}</td>

              {/* Stock cell — inline editable */}
              <td className="py-3 px-4 text-right">
                {isEditingThisStock ? (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <input
                      autoFocus
                      type="number"
                      min="0"
                      step="1"
                      value={editingStock.value}
                      onChange={e => setEditingStock(prev => ({ ...prev, value: e.target.value }))}
                      onFocus={e => e.target.select()}
                      onKeyDown={e => { if (e.key === 'Enter') saveStockEdit(e); if (e.key === 'Escape') cancelStockEdit(e); }}
                      className="w-20 text-right border border-blue-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button onClick={saveStockEdit} disabled={savingStock} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-40">
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={cancelStockEdit} className="p-1 text-muted-foreground hover:bg-muted rounded">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-1">
                    <span className="font-semibold">{p.stock ?? '—'}</span>
                    {onStockEdit && (
                      <button
                        onClick={e => startStockEdit(e, p)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-blue-600 rounded transition-opacity"
                        title="Edit stock quantity"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </td>

              <td className="py-3 px-4 text-right">
                {p._effectiveCost != null
                  ? <span className="font-semibold">₹{p._effectiveCost.toFixed(2)}</span>
                  : <span className="text-muted-foreground/40 text-xs">—</span>
                }
              </td>
              <td className="py-3 px-4 text-right">
                {p._effectiveSell != null
                  ? <span className="font-semibold">₹{p._effectiveSell.toFixed(2)}</span>
                  : <span className="text-muted-foreground/40 text-xs">—</span>
                }
              </td>
              <td className="py-3 px-4 pr-8 text-right">
                {p._margin != null
                  ? <span className={`font-semibold ${marginColor}`}>{p._margin.toFixed(1)}%</span>
                  : <span className="text-muted-foreground/40 text-xs">—</span>
                }
              </td>
              {batchTrackingEnabled && (
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  {p.expiry_date ? p.expiry_date.slice(0, 10) : '—'}
                </td>
              )}
            </tr>
          );
        })}
        {sorted.length === 0 && (
          <tr>
            <td colSpan={colSpan} className="py-8 text-center text-muted-foreground">
              <div className="space-y-1">
                <p>No stock rows found for this store.</p>
                <p className="text-xs">Upload and confirm an Opening/Closing Stock CSV in Appendix to populate the Stock page.</p>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
    </div>
  );
};

export default AllProductsTab;
